import React, { useEffect, useMemo, useState } from "react";
import { Box, LinearProgress, Divider } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTable } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark, faDownload, faMinus, faSquare } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { useResultsLogic } from "./Components/resultsLogic";

import TablePreset from "../Table/TablePreset";
import PageTemplate from "../Templates/PageTemplate";
import Filters from "../Filters/Filters";

import "./ImportExport.css";
import { BackButton, ButtonBlue } from "../HouseStyle/Components/Buttons";

function Results() {
    const navigate = useNavigate();
    const { import_id } = useParams();
    const [pagination, setPagination] = useState({
        page_size: 10,
        page: 1,
        max_items: 0,
        begin_of_page: 0,
        end_of_page: 0,
    });
    const [order, setOrder] = useState({ orderBy: "", direction: "" });
    
    // Filter state management
    const defaultFilters = [
        { 
            index: 0, 
            order: 1, 
            name: "Status", 
            field: 'status', 
            type: "dropdown", 
            options: [
                { name: "Toegevoegd", value: 0 },
                { name: "Bijgewerkt", value: 1 },
                { name: "Ongewijzigd", value: 2 },
                { name: "Fout", value: 3 }
            ]
        },
    ];
    const [tempfilters, setTempFilters] = useState(defaultFilters);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filters, setFilters] = useState(defaultFilters);
    
    const filterString = () => {
        if (filters.filter(e => e.value).length) {
            let string = "&filtering=";
            filters.filter(e => e.value).forEach((e) => {
                switch (e.type) {
                    case 'dropdown':
                        string += e.field;
                        string += "=";
                        string += e.value;
                        string += "|"
                        break;
                    default:
                        break;
                }
            });
            return string;
        }
        return "";
    };
    
    const CloseFilterModal = () => {
        setTempFilters([...filters]);
        setShowFilterModal(false);
    };
    
    // Componen that's containing the main logic
    const {
        loading,
        downloadable,
        importResults,
        importDetails,
        progressCount,
        succesfulCount,
        processedCount,
        pressedCancel,
        statusText,
        currentTableOptions,
        fetchAll,
        handleCancel,
        downloadOriginalFile,
    } = useResultsLogic(import_id, order, pagination, setPagination, filterString());

    // Columns for the table
    const columns = useMemo(() => [
        {
            Header: () => (
                <>
                    Status
                </>
            ),
            accessor: "status",
            Cell: ({ row }) => {
                let statusIcon = <p>?</p>;
                const statusClass = "d-flex justify-content-center";

                if (row.original.status === 0 || row.original.status === 1) {
                    statusIcon = <FontAwesomeIcon className="status-text-green" icon={faCheck} />;
                } else if (row.original.status === 2) {
                    statusIcon = <FontAwesomeIcon className="status-text-unchanged" icon={faMinus} />;
                } else if (row.original.status === 3) {
                    statusIcon = <FontAwesomeIcon className="status-text-red" icon={faXmark} />;
                }
                return <div className={statusClass}>{statusIcon}</div>;
            },
        },
        {
            Header: () => (
                <>
                    Message
                </>
            ),
            accessor: "internal_id",
            Cell: ({ row }) => (
                <div dangerouslySetInnerHTML={{ __html: row.original.message }} />
            ),
        },
    ], [order.orderBy, order.direction, currentTableOptions]);
    const tableInstance = useTable({ columns, data: importResults });
    const totalRows = importDetails?.total_rows ?? 0;
    const processedPercentage = Number.isFinite(progressCount) ? progressCount : 0;
    const successRate = processedCount === 0 ? "0.00" : ((succesfulCount / processedCount) * 100).toFixed(2);
    const failedCount = Math.max(processedCount - succesfulCount, 0);
    const formatDateSafe = (value, fallback) => {
        if (!value) {
            return fallback;
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return fallback;
        }

        return format(parsedDate, "dd-MM-yyyy HH:mm:ss");
    };
    const formattedStartDate = formatDateSafe(importDetails?.created_at, "Onbekende datum");
    const statusCode = importDetails?.status;
    const isAwaitingStart = statusCode === 3;

    // Fetch upon first load and when changing table settings
    useEffect(() => {
        fetchAll(true);
    }, [pagination.page_size, pagination.page, order.orderBy, order.direction, filters]);

    return (
        <PageTemplate navbarTitle={"Import & Export HUB"} pageIsLoading={loading}>
            <div className="import-page import-layout">
                <div className="import-page-header">
                    <div className="d-flex flex-wrap gap-5 align-items-center">
                        <BackButton text={`Terug`} onClickFunction={() => navigate('/import-export-center#imports')} />
                        <div className="d-flex flex-column gap-1">
                            <h1 className="HUBTitle mb-0">Importresultaten</h1>
                            <p className="FlatText import-header-subtitle mb-0">
                                Bekijk de voortgang en details van je importbewerking in één overzicht.
                            </p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="import-status-chip">
                            <span className="import-status-chip__label">Status</span>
                            {statusText}
                        </div>
                        <ButtonBlue
                            text={
                                <span className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faDownload} /> Download bestand
                                </span>
                            }
                            disabled={!downloadable}
                            onClickFunction={downloadOriginalFile}
                        />
                    </div>
                </div>

                {!isAwaitingStart ? (
                    <>
                        <div className="import-card import-card-compact">
                            <div className="d-flex flex-wrap align-items-center gap-4">
                                <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: "300px" }}>
                                    <div>
                                        <h2 className="BlockTitle mb-0">{processedPercentage.toFixed(1)}%</h2>
                                        <p className="BeneathFlatText text-muted mb-0">
                                            {processedCount} / {totalRows}
                                        </p>
                                    </div>
                                    <div className="flex-grow-1">
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Box sx={{ width: "100%", mr: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={processedPercentage}
                                                    sx={{
                                                        height: "8px",
                                                        borderRadius: "12px",
                                                        bgcolor: "rgba(2, 90, 122, 0.12)",
                                                        "& .MuiLinearProgress-bar": {
                                                            background: "linear-gradient(90deg, #009FE3 0%, #025A7A 100%)",
                                                            borderRadius: "12px",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </div>
                                </div>
                                <div className="d-flex gap-4 flex-wrap">
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon className="status-text-green" icon={faCheck} />
                                        <div>
                                            <p className="BeneathFlatText text-muted mb-0">Geslaagd</p>
                                            <p className="FlatText fw-bold mb-0">{succesfulCount}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon className="status-text-red" icon={faXmark} />
                                        <div>
                                            <p className="BeneathFlatText text-muted mb-0">Mislukt</p>
                                            <p className="FlatText fw-bold mb-0">{failedCount}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="import-card import-card-compact mt-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
                                <div className="d-flex flex-wrap gap-4 flex-grow-1">
                                    <div>
                                        <p className="import-section-label mb-1">Type</p>
                                        <p className="FlatText mb-0 fw-semibold">{currentTableOptions?.DisplayName ?? "-"}</p>
                                    </div>
                                    <div>
                                        <p className="import-section-label mb-1">Door wie</p>
                                        <p className="FlatText mb-0 fw-semibold">{importDetails?.username ?? "Onbekend"}</p>
                                    </div>
                                    <div>
                                        <p className="import-section-label mb-1">Gestart</p>
                                        <p className="FlatText mb-0 fw-semibold">{formattedStartDate}</p>
                                    </div>
                                    <div>
                                        <p className="import-section-label mb-1">Succesratio</p>
                                        <p className="FlatText mb-0 fw-semibold">{succesfulCount}/{processedCount} ({successRate}%)</p>
                                    </div>
                                    <div>
                                        <p className="import-section-label mb-1">Instellingen</p>
                                        <p className="FlatText mb-0">
                                            {importDetails?.updating ? (
                                                <span className="status-text-green"><FontAwesomeIcon icon={faCheck} /> Update</span>
                                            ) : (
                                                <span className="status-text-red"><FontAwesomeIcon icon={faXmark} /> Alleen toevoegen</span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2 align-items-start flex-wrap">
                                    {(statusCode === 2 || statusCode === 3) && !pressedCancel && (
                                        <button onClick={handleCancel} className="import-cancel-button">
                                            <FontAwesomeIcon icon={faXmark} className="me-2" /> Annuleer
                                        </button>
                                    )}
                                </div>
                            </div>
                            <Divider className="my-3" />
                            <div className="d-flex flex-wrap gap-3 align-items-center">
                                <p className="import-section-label mb-0">Legenda:</p>
                                <div className="d-flex flex-wrap gap-3">
                                    <p className="FlatText mb-0">
                                        <FontAwesomeIcon className="status-text-green me-1" icon={faSquare} />
                                        <span>Toegevoegd</span>
                                    </p>
                                    <p className="FlatText mb-0">
                                        <FontAwesomeIcon className="status-text-orange me-1" icon={faSquare} />
                                        <span>Bijgewerkt</span>
                                    </p>
                                    <p className="FlatText mb-0">
                                        <FontAwesomeIcon className="status-text-red me-1" icon={faSquare} />
                                        <span>Fout</span>
                                    </p>
                                    <p className="FlatText mb-0">
                                        <FontAwesomeIcon className="status-text-unchanged me-1" icon={faSquare} />
                                        <span>Ongewijzigd</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="import-status-card d-flex align-items-center gap-3">
                        <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                            aria-hidden="true"
                        ></div>
                        <p className="FlatText mb-0">
                            Het importeren is nog niet gestart. Zodra het proces loopt verschijnen hier de details (maximaal 1 minuut).
                        </p>
                    </div>
                )}

                <div className="mt-4">
                    <TablePreset
                        data={[
                            {
                                tableInstance: tableInstance,
                                pagination: pagination,
                                primary_key: "consumer_id",
                            },
                        ]}
                        setPagination={setPagination}
                        handleSelection={() => {}}
                        leftButtons={[{ title: "Filteren", function: () => setShowFilterModal(true) }]}
                    />
                </div>
                <Filters
                    filters={tempfilters}
                    props={{}}
                    showFilterModal={showFilterModal}
                    closeFilterModal={CloseFilterModal}
                    setTempFilters={setTempFilters}
                    setFilters={setFilters}
                    defaultFilters={defaultFilters}
                />
            </div>
        </PageTemplate>
    );
}

export default Results;
